/**
 * @file: useArchivedFeedThreads.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Paginated loader for the archived-threads view. A single server-side
 * `archivedOnly` stream — the backend returns only archived threads, so no
 * client-side filtering of a mixed page.
 */

import { FeedThreadInboxItem } from '@euler/model/feed';
import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn } from '@euler/utils/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';

const kPageSize = 30;

export interface UseArchivedFeedThreadsResult {
  threads: FeedThreadInboxItem[];
  loading: boolean;
  refreshing: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: Error | undefined;
  refresh: (options?: { silent?: boolean }) => Promise<void>;
  loadMore: () => Promise<void>;
  /** Drop a thread locally (after restore). */
  removeThread: (id: string) => void;
}

export const useArchivedFeedThreads = (): UseArchivedFeedThreadsResult => {
  const { feedService } = useServiceFactory();
  const [threads, setThreads] = useState<FeedThreadInboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<Error | undefined>(undefined);
  const reqRef = useRef(0);

  const fetchPage = useCallback(
    async (cursor: string | undefined) => {
      const reqId = ++reqRef.current;
      const res = await feedService.listThreads({
        archivedOnly: true,
        cursor,
        limit: kPageSize,
      });
      if (reqId !== reqRef.current) return null;
      return res;
    },
    [feedService],
  );

  const refresh = usePersistFn(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setRefreshing(true);
    setError(undefined);
    try {
      const res = await fetchPage(undefined);
      if (res) {
        setThreads(res.items);
        setNextCursor(res.nextCursor);
      }
    } catch (e) {
      setError(e as Error);
    } finally {
      if (!options?.silent) setRefreshing(false);
      setLoading(false);
    }
  });

  const loadMore = usePersistFn(async () => {
    if (loadingMore || nextCursor == null) return;
    setLoadingMore(true);
    try {
      const res = await fetchPage(nextCursor);
      if (res) {
        setThreads(prev => [...prev, ...res.items]);
        setNextCursor(res.nextCursor);
      }
    } catch (e) {
      setError(e as Error);
    } finally {
      setLoadingMore(false);
    }
  });

  useEffect(() => {
    void refresh({ silent: true });
  }, [refresh]);

  const removeThread = useCallback((id: string) => {
    setThreads(prev => prev.filter(t => t.id !== id));
  }, []);

  return {
    threads,
    loading,
    refreshing,
    loadingMore,
    hasMore: nextCursor != null,
    error,
    refresh,
    loadMore,
    removeThread,
  };
};
